import { resVerCall, resDataCall, init as initApi } from '@/apicall';
import { ResourceData, ResourceVersion } from '@/protos/resourcedata';

type OnlyArray<T> = T extends Array<infer V> ? V[] : never;

type ListTypeKeyof<T> = {
    [P in keyof T]-?: T[P] extends OnlyArray<T[P]> ? P : never;
}[keyof T];

class IDBHelper {
    private dbName = 'DilMeterResources';
    private dbVersion = 1;
    private storeName = 'resources';
    private dbPromise: Promise<IDBDatabase> | null = null;

    private open(): Promise<IDBDatabase> {
        if (this.dbPromise) return this.dbPromise;
        this.dbPromise = new Promise((resolve, reject) => {
            const req = indexedDB.open(this.dbName, this.dbVersion);
            req.onupgradeneeded = (evt) => {
                const db = (evt.target as any).result;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName);
                }
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
        return this.dbPromise;
    }

    public async get(key: string): Promise<any> {
        try {
            const db = await this.open();
            return new Promise((resolve) => {
                const tx = db.transaction(this.storeName, 'readonly');
                const req = tx.objectStore(this.storeName).get(key);
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => resolve(undefined); // Fail safe
            });
        } catch { return undefined; }
    }

    public async put(key: string, val: any): Promise<void> {
        try {
            const db = await this.open();
            const tx = db.transaction(this.storeName, 'readwrite');
            tx.objectStore(this.storeName).put(val, key);
        } catch { /* ignore */ }
    }
}

export class MabiDB {
    private cache: Partial<ResourceData> = {};
    private idb = new IDBHelper();

    private endpointMap: Partial<Record<keyof ResourceData, string>> = {
        SkillNameMap: '/api/resources/skills',
        StatusNameMap: '/api/resources/status',
        RaceNameMap: '/api/resources/race',
        ItemList: '/api/resources/items',
    };

    private static SkillOverrides: Record<number, string> = {
        59028: "Flashing Steel",
        59080: "Pledge of Penance",
        59081: "Provoke",
        59082: "Ardent Advance",
        59083: "Rend Armor",
        59084: "Deliver Judgment",
        59085: "Divine Retribution",
        59086: "Martyr's Consecration",
        59140: "Elemental Compounding",
        59141: "Compound Catalyst",
        59142: "Elemental Amplification",
        59143: "Serpentcall",
        59144: "Chemical Carnival",
        59145: "Fangs of Stone",
        59120: "Bullet Barrage",
        59121: "Heavy Artillery",
        59122: "Calamity Cannon",
        59123: "Fatal Scope",
        59124: "Tracer Pod",
        59125: "Detonation Field",
        59126: "Gunner's Crest",
        59100: "Recharge Shells",
        59101: "Relentless Thrust",
        59102: "Arc Impact",
        59103: "Blast Zone",
        59104: "Drill Charge",
        59105: "Dreadnought Burst",
        59106: "Blaze of Glory"
    };

    public constructor(private region: string, private lang: string) {
    }

    public async tryOpen(): Promise<void> {
    }

    public async getData<T extends keyof ResourceData>(key: T, region = this.region): Promise<ResourceData[T] | undefined> {
        // 1. Check Memory Cache (valid for current session only)
        if (this.cache[key] !== undefined) {
            return this.cache[key] as ResourceData[T];
        }

        // 2. Fetch from API endpoint (always fresh from resourcedb.sqlite)
        const endpoint = this.endpointMap[key];
        if (endpoint) {
            try {
                const res = await fetch(endpoint);
                if (!res.ok) throw new Error(`Failed to fetch ${key}`);

                const data = await res.json();

                // Patch
                if (key === 'SkillNameMap') {
                    Object.assign(data, MabiDB.SkillOverrides);
                }

                this.cache[key] = data;
                console.log(`[MabiDB] Fetched ${key} from API`);

                return data as ResourceData[T];
            } catch (e) {
                console.warn(`MabiDB lazy load failed for ${key}`, e);
                return undefined;
            }
        }

        // 3. Fallback for lists that don't have endpoints yet (return empty array to avoid crashes)
        // Check if expected type is Array
        if (key.endsWith('List')) {
            return [] as any;
        }

        return undefined;
    }

    public async getSortedListData<T extends ListTypeKeyof<ResourceData>>(key: T, region = this.region): Promise<ResourceData[T]> {
        const data = await this.getData(key, region);
        const l = (data as any) || [];
        if (!Array.isArray(l)) return [] as any;

        // Sort in place (copy first?)
        // The cache stores the raw data. We should probably clone if we sort?
        // Or just sort the cached one if order doesn't matter or is consistent.
        // Let's copy.
        const sorted = [...l];
        sorted.sort((a, b) => {
            const aKey = keyGetter(key, a);
            const bKey = keyGetter(key, b);
            return aKey - bKey;
        });
        return sorted as ResourceData[T];
    }

    public getCurLangString(key: string): string {
        // String table support if needed
        return key;
    }

    public getCurLangStrings(keys: string[]): string[] {
        return keys.map(key => this.getCurLangString(key));
    }
}

// KeyGetter helper remains


function keyGetter<T extends ListTypeKeyof<ResourceData>>(typeName: T, d: ResourceData[T][0]): number {
    if (typeName == 'AchievementList') {
        const achievement = d as ResourceData['AchievementList'][0];
        return achievement.Id;
    }
    else if (typeName == 'BarterList') {
        const barter = d as ResourceData['BarterList'][0];
        return barter.Id;
    }
    else if (typeName == 'CharCondList') {
        const charCond = d as ResourceData['CharCondList'][0];
        return charCond.Id;
    }
    else if (typeName == 'FoodList') {
        const food = d as ResourceData['FoodList'][0];
        return food.Id;
    }
    else if (typeName == 'ItemExtendMetalWareList') {
        const item = d as ResourceData['ItemExtendMetalWareList'][0];
        return item.Id;
    }
    else if (typeName == 'ItemExtendUpgradeList') {
        const item = d as ResourceData['ItemExtendUpgradeList'][0];
        return item.Id;
    }
    else if (typeName == 'ItemList') {
        const item = d as ResourceData['ItemList'][0];
        return item.Id;
    }
    else if (typeName == 'ItemUpgradeList') {
        const item = d as ResourceData['ItemUpgradeList'][0];
        return item.Id;
    }
    else if (typeName == 'MetalWareAbilityList') {
        const item = d as ResourceData['ItemExtendMetalWareList'][0];
        return item.Id;
    }
    else if (typeName == 'MetalWareItemList') {
        const item = d as ResourceData['MetalWareItemList'][0];
        return item.Id;
    }
    else if (typeName == 'MetalWareLevelList') {
        const item = d as ResourceData['MetalWareLevelList'][0];
        return item.Level;
    }
    else if (typeName == 'MiniatureList') {
        const item = d as ResourceData['MiniatureList'][0];
        return item.Id;
    }
    else if (typeName == 'OptionSetList') {
        const item = d as ResourceData['OptionSetList'][0];
        return item.Id;
    }
    else if (typeName == 'PetList') {
        const item = d as ResourceData['PetList'][0];
        return item.Id;
    }
    else if (typeName == 'ProductionList') {
        const item = d as ResourceData['ProductionList'][0];
        return item.ItemId;
    }
    else if (typeName == 'RaceList') {
        const item = d as ResourceData['RaceList'][0];
        return item.Id;
    }
    else if (typeName == 'RandomTableList') {
        const item = d as ResourceData['RandomTableList'][0];
        return item.Id;
    }
    else if (typeName == 'SkillList') {
        const item = d as ResourceData['SkillList'][0];
        return item.Id;
    }
    else if (typeName == 'SocialMotionList') {
        const item = d as ResourceData['SocialMotionList'][0];
        return item.Id;
    }
    else if (typeName == 'TalentList') {
        const item = d as ResourceData['TalentList'][0];
        return item.Id;
    }
    else if (typeName == 'TitleList') {
        const item = d as ResourceData['TitleList'][0];
        return item.Id;
    }

    throw new Error(`keyGetter: unknown typeName ${typeName}`);
}